import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { notifyXp } from '@/lib/xpToast';
import { Play, Pause, RotateCcw, Trophy, ArrowLeft, Zap, X } from 'lucide-react';

const ROUND_MS = 60_000;
const SPAWN_MS = 900;

export default function ThoughtDefense() {
  const { user } = useAuth();
  const [pool, setPool] = useState({ negative: [], positive: [] });
  const [bubbles, setBubbles] = useState([]);
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [phase, setPhase] = useState('ready'); // ready | playing | done
  const [timeLeft, setTimeLeft] = useState(60);
  const [highScore, setHighScore] = useState(0);
  const [flash, setFlash] = useState(null);
  const arenaRef = useRef(null);
  const intervals = useRef({});

  useEffect(() => {
    api.get('/games/thought-bubbles').then(r => setPool(r.data));
    if (user) api.get('/games/state').then(r => setHighScore(r.data?.thought_defense?.high_score || 0)).catch(() => {});
  }, [user]);

  const spawn = useCallback(() => {
    const isNeg = Math.random() < 0.7;
    const bank = isNeg ? pool.negative : pool.positive;
    if (!bank?.length) return;
    const text = bank[Math.floor(Math.random() * bank.length)];
    const arena = arenaRef.current; if (!arena) return;
    const w = arena.offsetWidth, h = arena.offsetHeight;
    const b = {
      id: `${Date.now()}-${Math.random()}`, text, isNeg,
      x: 20 + Math.random() * (w - 200), y: h + 40,
      vy: -1.2 - Math.random() * 1.4,
      vx: (Math.random() - 0.5) * 0.6,
      rot: (Math.random() - 0.5) * 8,
    };
    setBubbles((arr) => [...arr, b]);
  }, [pool]);

  const tick = useCallback(() => {
    setBubbles((arr) => arr.map(b => ({...b, x: b.x + b.vx, y: b.y + b.vy})).filter(b => {
      if (b.y < -80) {
        // Negative escaped = miss; positive escaped = fine
        if (b.isNeg) setMisses(m => m + 1);
        return false;
      }
      return true;
    }));
  }, []);

  const start = () => {
    setBubbles([]); setScore(0); setMisses(0); setTimeLeft(60); setPhase('playing');
    intervals.current.spawn = setInterval(spawn, SPAWN_MS);
    intervals.current.tick = setInterval(tick, 30);
    intervals.current.clock = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { end(); return 0; }
        return t - 1;
      });
    }, 1000);
  };
  const stopAll = () => { Object.values(intervals.current).forEach(clearInterval); intervals.current = {}; };
  const end = async () => {
    stopAll(); setPhase('done');
    if (user) {
      try {
        // XP is computed server-side from raw `score` (admin can tune the multiplier in Games admin).
        const r = await api.post('/games/record', { game: 'thought_defense', score });
        setHighScore(r.data.thought_defense?.high_score || score);
        notifyXp(r.data.xp_result, 'Thought Defense');
        window.dispatchEvent(new Event('kc:xp-updated'));
      } catch {}
    }
  };
  useEffect(() => () => stopAll(), []);

  const hit = (b) => {
    if (b.revealed) return;
    setFlash({ id: b.id, ok: b.isNeg });
    setTimeout(() => setFlash(null), 250);
    // Reveal: keep bubble onscreen briefly in its true color so the user learns.
    setBubbles((arr) => arr.map(x => x.id === b.id ? { ...x, revealed: true, vy: 0, vx: 0 } : x));
    setTimeout(() => setBubbles((arr) => arr.filter(x => x.id !== b.id)), 600);
    setScore((s) => s + (b.isNeg ? 10 : -5));
    try { beep(b.isNeg ? 880 : 220); } catch {}
  };

  return (
    <div className="bg-ink text-white min-h-screen">
      <div className="kc-container py-6">
        <Link to="/games" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-[color:var(--kc-gold)]" data-testid="back-games"><ArrowLeft size={12}/> Games</Link>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Zap size={12}/> 60-Second Drill</div>
            <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Thought Defense</h1>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Stat label="Score" value={score} testid="td-score"/>
            <Stat label="Time" value={`${timeLeft}s`} testid="td-time"/>
            <Stat label="Best" value={highScore} testid="td-best"/>
          </div>
        </div>

        <div ref={arenaRef} className="relative mt-5 h-[60vh] min-h-[420px] rounded-[var(--kc-radius-md)] overflow-hidden border-2 border-white/10 bg-[#0E0E11]" style={{ background: 'radial-gradient(600px 400px at 50% 40%, rgba(212,164,55,0.08), #0B0B0C 60%)' }} data-testid="td-arena">
          {/* Goal line at top */}
          <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-[color:var(--kc-gold)]/30 to-transparent pointer-events-none"/>
          <div className="absolute top-12 left-0 right-0 border-t border-dashed border-[color:var(--kc-gold)]/40 pointer-events-none"/>
          <div className="absolute top-2 right-3 text-[10px] uppercase tracking-wider text-[color:var(--kc-gold)]/80">YOUR HEADSPACE</div>

          {phase === 'ready' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
              <h2 className="font-display uppercase text-4xl md:text-5xl">Defend your mind.</h2>
              <p className="mt-3 text-white/75 max-w-md leading-relaxed">Thoughts will fly in <span className="font-bold">looking the same</span>. Read them. Tap to <span className="font-bold">dismiss</span>. The bubble reveals: <span className="text-red-300 font-bold">red = negative</span> (great dismiss), <span className="text-emerald-300 font-bold">green = positive</span> (oops, that was a good one).</p>
              <p className="mt-2 text-xs text-white/50 max-w-md">+10 for dismissing a negative · -5 for dismissing a positive · let positives pass for safety.</p>
              <button onClick={start} className="mt-6 h-14 px-8 inline-flex items-center gap-2 rounded-full bg-[color:var(--kc-gold)] text-black font-semibold text-lg hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="td-start"><Play size={20} fill="currentColor"/> Start round</button>
              {!user && <p className="mt-4 text-xs text-white/50">Log in to save your score and earn XP.</p>}
            </div>
          )}

          {phase === 'playing' && bubbles.map((b) => {
            // Neutral until tapped; on reveal flash green (positive) or red (negative).
            const cls = b.revealed
              ? (b.isNeg
                  ? 'bg-red-500 text-white border-2 border-red-200 ring-4 ring-red-500/40 scale-110'
                  : 'bg-emerald-500 text-white border-2 border-emerald-200 ring-4 ring-emerald-500/40 scale-110')
              : 'bg-[#F4ECDE] text-ink border border-white/40 hover:bg-white';
            return (
              <button
                key={b.id}
                onPointerDown={() => hit(b)}
                disabled={b.revealed}
                className={`absolute px-4 py-2 rounded-full font-semibold text-sm md:text-base whitespace-nowrap shadow-lg transition-all duration-300 active:scale-90 ${cls}`}
                style={{ left: b.x, top: b.y, transform: `rotate(${b.revealed ? 0 : b.rot}deg)` }}
                data-testid={`td-bubble-${b.revealed ? (b.isNeg ? 'neg' : 'pos') : 'neutral'}`}
              >
                {b.text}
              </button>
            );
          })}

          {phase === 'done' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-black/60 backdrop-blur-sm">
              <Trophy size={48} className="text-[color:var(--kc-gold)]"/>
              <h2 className="mt-4 font-display uppercase text-4xl">Round Complete.</h2>
              <div className="mt-4 grid grid-cols-3 gap-6 text-center">
                <div><div className="font-display text-4xl text-[color:var(--kc-gold)]" data-testid="td-final-score">{score}</div><div className="text-[10px] uppercase tracking-wider text-white/60">Score</div></div>
                <div><div className="font-display text-4xl">{misses}</div><div className="text-[10px] uppercase tracking-wider text-white/60">Missed</div></div>
                <div><div className="font-display text-4xl text-[color:var(--kc-gold)]">{highScore}</div><div className="text-[10px] uppercase tracking-wider text-white/60">Best</div></div>
              </div>
              <p className="mt-4 text-xs text-white/50">XP awarded — check the bar in the header.</p>
              <div className="mt-6 flex gap-3">
                <button onClick={() => { setPhase('ready'); }} className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold" data-testid="td-again"><RotateCcw size={16}/> Play again</button>
                <Link to="/games" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-white/20 hover:bg-white/10 font-semibold">Other games</Link>
              </div>
            </div>
          )}

          {flash && (
            <div className={`absolute inset-0 pointer-events-none animate-pulse ${flash.ok ? 'bg-emerald-500/20' : 'bg-red-500/20'}`} style={{ animationDuration: '0.25s' }}/>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="text-center"><div className="font-display text-2xl text-[color:var(--kc-gold)] tabular-nums" data-testid={testid}>{value}</div><div className="text-[10px] uppercase tracking-wider text-white/60">{label}</div></div>
);

let audioCtx = null;
function beep(freq = 440) {
  if (!audioCtx) { try { audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return; } }
  const o = audioCtx.createOscillator(); const g = audioCtx.createGain();
  o.type = 'sine'; o.frequency.value = freq; g.gain.value = 0.06;
  o.connect(g); g.connect(audioCtx.destination);
  o.start(); o.stop(audioCtx.currentTime + 0.08);
}
